/**
 * Keep the focused field visible when the mobile keyboard (or native
 * date sheet) covers the lower visual viewport.
 *
 * Nested `overflow-y-auto` panes — Spatialist Field mission detail,
 * SpaceStation visit detail, SpaceManager drawers — do not follow
 * `visualViewport` the way document scroll does. iOS therefore leaves
 * Reason for schedule change / date / time / notes under the keyboard
 * until the operator hides it and scrolls. This helper:
 *
 *   1. Adds temporary padding on the nearest scroller so there is room
 *      to move the field up.
 *   2. Scrolls that scroller until the field sits in the visible band
 *      above the keyboard and the bottom hub (FIELD / STATION / ME).
 *
 * Mounted once from the root layout so every portal shares it.
 */

export const KEYBOARD_INSET_VAR = "--vs-keyboard-inset";

/**
 * Height of a fixed / sticky Prev-Next footer (the order wizard's), set on
 * `<html>` while one is on screen. The input scroll margin in globals.css
 * adds it, so native `scrollIntoView` and focus scrolling leave a field
 * above the footer; the footer itself also carries
 * `data-keyboard-chrome="bottom"`, which this helper measures directly.
 */
export const WIZARD_FOOTER_HEIGHT_VAR = "--vs-wizard-footer-height";

/** Tallest of the given bars that is displayed (0 when none is). */
export function visibleBarHeightPx(
  nodes: Iterable<HTMLElement | null | undefined>,
): number {
  let height = 0;
  for (const node of nodes) {
    if (!node || !node.isConnected) continue;
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") continue;
    height = Math.max(height, node.getBoundingClientRect().height);
  }
  return Math.round(height);
}

export function writeWizardFooterHeightVar(
  px: number,
  root: HTMLElement | null = typeof document === "undefined"
    ? null
    : document.documentElement,
): void {
  if (!root) return;
  if (!(px > 0)) {
    root.style.removeProperty(WIZARD_FOOTER_HEIGHT_VAR);
    return;
  }
  root.style.setProperty(WIZARD_FOOTER_HEIGHT_VAR, `${Math.round(px)}px`);
}

/** Overlap smaller than this is URL-bar chrome, not a keyboard. */
export const KEYBOARD_OVERLAP_THRESHOLD_PX = 100;

/**
 * A `visualViewport` resize must change the visible height by at least
 * this much (with the keyboard open) before we re-scroll the focused field.
 * Catches keyboard open and the iOS predictive bar toggling; ignores the
 * sub-pixel / URL-bar jitter that fires while the finger pans.
 */
export const KEYBOARD_RESCROLL_HEIGHT_DELTA_PX = 32;

/**
 * Decide whether a viewport resize should pull the focused field back into
 * view. Only a keyboard that just opened or materially changed height
 * qualifies. `visualViewport.scroll` never qualifies — with the keyboard up,
 * iOS fires it continuously while the user pans, and re-scrolling there
 * snaps the page back to the field on every frame.
 */
export function shouldRescrollOnViewportResize(args: {
  previousHeight: number | null;
  nextHeight: number;
  overlapPx: number;
}): boolean {
  if (!isKeyboardOpen(args.overlapPx)) return false;
  if (args.previousHeight === null) return true;
  return (
    Math.abs(args.nextHeight - args.previousHeight) >=
    KEYBOARD_RESCROLL_HEIGHT_DELTA_PX
  );
}

const SKIP_INPUT_TYPES = new Set([
  "button",
  "submit",
  "reset",
  "checkbox",
  "radio",
  "file",
  "hidden",
  "image",
  "range",
  "color",
]);

type PaddedScroller = {
  el: HTMLElement;
  previousPaddingBottom: string;
};

let paddedScroller: PaddedScroller | null = null;

export type VisualViewportBox = {
  top: number;
  bottom: number;
  height: number;
};

export function keyboardOverlapPx(
  innerHeight: number = typeof window === "undefined" ? 0 : window.innerHeight,
  visual: Pick<
    VisualViewport,
    "height" | "offsetTop"
  > | null = typeof window === "undefined" ? null : window.visualViewport,
): number {
  if (!visual) return 0;
  return Math.max(
    0,
    Math.round(innerHeight - visual.height - (visual.offsetTop ?? 0)),
  );
}

export function isKeyboardOpen(overlapPx: number): boolean {
  return overlapPx >= KEYBOARD_OVERLAP_THRESHOLD_PX;
}

export function isKeyboardScrollField(
  el: EventTarget | null,
): el is
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement
  | HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.closest("[data-keyboard-scroll-skip]")) return false;
  if (el instanceof HTMLTextAreaElement) {
    return !el.disabled && !el.readOnly;
  }
  if (el instanceof HTMLSelectElement) {
    return !el.disabled;
  }
  if (el instanceof HTMLInputElement) {
    if (el.disabled) return false;
    const type = (el.type || "text").toLowerCase();
    if (SKIP_INPUT_TYPES.has(type)) return false;
    if (
      el.readOnly &&
      type !== "date" &&
      type !== "time" &&
      type !== "datetime-local"
    ) {
      return false;
    }
    return true;
  }
  return el.isContentEditable === true;
}

export function resolveKeyboardScrollTarget(el: HTMLElement): HTMLElement {
  const anchor = el.closest("[data-keyboard-scroll-anchor]");
  return anchor instanceof HTMLElement ? anchor : el;
}

export function hasVerticalOverflowStyle(el: HTMLElement): boolean {
  const overflowY = String(window.getComputedStyle(el).overflowY);
  return (
    overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay"
  );
}

export function isVerticallyScrollable(el: HTMLElement): boolean {
  return hasVerticalOverflowStyle(el) && el.scrollHeight > el.clientHeight + 1;
}

/**
 * Prefer an already-overflowing ancestor. If the nested Field pane is
 * flush with its content (common before the keyboard steals height),
 * still return that overflow:auto pane so we can pad it and then scroll.
 */
export function nearestVerticalScroller(from: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = from.parentElement;
  let overflowPane: HTMLElement | null = null;
  while (node) {
    if (hasVerticalOverflowStyle(node)) {
      if (node.scrollHeight > node.clientHeight + 1) return node;
      if (!overflowPane) overflowPane = node;
    }
    node = node.parentElement;
  }
  if (overflowPane) return overflowPane;
  const doc = document.scrollingElement;
  return doc instanceof HTMLElement ? doc : document.documentElement;
}

export function visualViewportBox(
  visual: Pick<
    VisualViewport,
    "height" | "offsetTop"
  > | null = typeof window === "undefined" ? null : window.visualViewport,
  innerHeight: number = typeof window === "undefined" ? 0 : window.innerHeight,
): VisualViewportBox {
  const height = Math.max(1, Math.round(visual?.height ?? innerHeight));
  const top = Math.max(0, Math.round(visual?.offsetTop ?? 0));
  return { top, height, bottom: top + height };
}

export function bottomChromeOverlapPx(
  vis: VisualViewportBox,
  root: ParentNode = document,
): number {
  let obstruction = 0;
  const nodes = root.querySelectorAll(
    'nav[aria-label="Primary"], [data-keyboard-chrome="bottom"]',
  );
  for (const node of nodes) {
    if (!(node instanceof HTMLElement)) continue;
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") continue;
    const rect = node.getBoundingClientRect();
    if (rect.height < 8) continue;
    const overlap =
      Math.min(rect.bottom, vis.bottom) - Math.max(rect.top, vis.top);
    if (overlap > 8 && rect.bottom >= vis.bottom - 12) {
      obstruction = Math.max(obstruction, overlap);
    }
  }
  return Math.round(obstruction);
}

function restoreScrollerPadding() {
  if (!paddedScroller) return;
  const { el, previousPaddingBottom } = paddedScroller;
  const top = el.scrollTop;
  el.style.paddingBottom = previousPaddingBottom;
  paddedScroller = null;
  // Dropping keyboard padding shrinks scrollHeight. Keep the
  // surveyor on the same offset instead of letting the browser
  // clamp them toward the top of a measurement card.
  el.scrollTop = top;
}

function ensureScrollerPadding(scroller: HTMLElement, extraPx: number) {
  if (extraPx <= 0) {
    restoreScrollerPadding();
    return;
  }
  if (paddedScroller && paddedScroller.el !== scroller) {
    restoreScrollerPadding();
  }
  if (!paddedScroller) {
    paddedScroller = {
      el: scroller,
      previousPaddingBottom: scroller.style.paddingBottom,
    };
  }
  scroller.style.paddingBottom = `${Math.round(extraPx)}px`;
}

export function clearKeyboardScrollPadding() {
  restoreScrollerPadding();
}

export function writeKeyboardInsetVar(
  overlapPx: number,
  root: HTMLElement | null = typeof document === "undefined"
    ? null
    : document.documentElement,
): void {
  if (!root) return;
  const inset = isKeyboardOpen(overlapPx) ? overlapPx : 0;
  if (inset <= 0) {
    root.style.removeProperty(KEYBOARD_INSET_VAR);
    return;
  }
  root.style.setProperty(KEYBOARD_INSET_VAR, `${inset}px`);
}

/**
 * Scroll `el` (or its `data-keyboard-scroll-anchor`) into the visual
 * viewport band above the keyboard and bottom hub.
 *
 * Returns the scroll delta applied to the nearest scroller (0 if none).
 */
export function scrollFocusedFieldIntoView(el: EventTarget | null): number {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }
  if (!isKeyboardScrollField(el)) return 0;

  const focused = el;
  const target = resolveKeyboardScrollTarget(focused);
  const scroller = nearestVerticalScroller(focused);
  if (!scroller) return 0;

  const overlap = keyboardOverlapPx();
  const vis = visualViewportBox();
  const chrome = bottomChromeOverlapPx(vis);
  const pad = 24;
  const usableTop = vis.top + pad;
  const usableBottom = vis.bottom - chrome - pad;
  if (usableBottom - usableTop < 48) return 0;

  if (isKeyboardOpen(overlap)) {
    // Spatialist's frame already shrinks to the visual viewport. Adding the
    // keyboard's full height as CSS padding would make the flex pane's minimum
    // box taller than that frame and push its footer out of view. Reserve only
    // the part of this particular scroller that is actually obstructed.
    const extra = document.documentElement.hasAttribute(
      "data-vs-spatialist-portal",
    )
      ? Math.min(
          overlap + chrome + pad,
          Math.max(pad, scroller.getBoundingClientRect().bottom - usableBottom),
        )
      : overlap + chrome + pad;
    ensureScrollerPadding(scroller, extra);
  } else {
    // Keyboard dismissed while the field is still focused (iOS
    // Done / hide). Drop the extra scroll range so the caret
    // cannot be parked under the hub on the next open.
    clearKeyboardScrollPadding();
  }

  const rect = focused.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const usableHeight = usableBottom - usableTop;
  const see =
    target !== focused && targetRect.height <= usableHeight ? targetRect : rect;

  let delta = 0;
  if (see.bottom > usableBottom) {
    delta = see.bottom - usableBottom;
    if (see.height > usableHeight) {
      delta = see.top - usableTop;
    }
  } else if (see.top < usableTop) {
    delta = see.top - usableTop;
  }

  if (Math.abs(delta) < 2) return 0;

  const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const next = Math.min(maxScroll, Math.max(0, scroller.scrollTop + delta));
  const applied = next - scroller.scrollTop;
  scroller.scrollTop = next;
  return applied;
}

/**
 * Bring a block into the band above the bottom chrome (the wizard's fixed
 * Prev/Next bar, the hub, an open keyboard) without focusing anything:
 * a blocked Next on a question that is not a text field (Purpose).
 *
 * A block that fits is centred in the band. A taller one lands the part
 * that matters: from `first` (its first option) down to `last` (its error
 * line), with `last` just above the bar so as much of the block above it
 * shows; when even that range is taller than the band, `first` goes to
 * the band's top. A plain centred `scrollIntoView` ignored the bar and
 * left a tall block's error half under it (switch-on round 3).
 *
 * Returns the scroll delta asked of the nearest scroller (0 if none).
 */
export function scrollBlockAboveBottomChrome(
  block: HTMLElement | null,
  opts: {
    first?: HTMLElement | null;
    last?: HTMLElement | null;
    behavior?: ScrollBehavior;
  } = {},
): number {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }
  if (!block || !block.isConnected) return 0;
  const scroller = nearestVerticalScroller(block);
  if (!scroller) return 0;

  const vis = visualViewportBox();
  const chrome = bottomChromeOverlapPx(vis);
  const pad = 24;
  const usableTop = vis.top + pad;
  const usableBottom = vis.bottom - chrome - pad;
  const usableHeight = usableBottom - usableTop;
  if (usableHeight < 48) return 0;

  const rect = block.getBoundingClientRect();
  let delta: number;
  if (rect.height <= usableHeight) {
    delta = rect.top + rect.height / 2 - (usableTop + usableHeight / 2);
  } else {
    const top = (opts.first ?? block).getBoundingClientRect().top;
    const bottom = (opts.last ?? block).getBoundingClientRect().bottom;
    delta = bottom - top <= usableHeight ? bottom - usableBottom : top - usableTop;
  }
  if (Math.abs(delta) < 2) return 0;

  const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const next = Math.min(maxScroll, Math.max(0, scroller.scrollTop + delta));
  const applied = next - scroller.scrollTop;
  if (opts.behavior === "smooth" && typeof scroller.scrollTo === "function") {
    scroller.scrollTo({ top: next, behavior: "smooth" });
  } else {
    scroller.scrollTop = next;
  }
  return applied;
}

/** A form control a blocked Next can focus. */
const FOCUSABLE_CONTROL =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
const INVALID_CONTROL =
  'input[aria-invalid="true"]:not([type="hidden"]):not([disabled]), select[aria-invalid="true"]:not([disabled]), textarea[aria-invalid="true"]:not([disabled])';

/**
 * The field a blocked Next focuses in the block it brings into view: the
 * block itself when it is a control (a size box), else its first invalid
 * control (a You field inside its shell), else a choice group's first
 * option (Purpose), else its first control. Null for a block with nothing
 * to type or pick (the step's error banner).
 */
export function blockedFieldFocusTarget(
  block: HTMLElement | null,
): HTMLElement | null {
  if (!block) return null;
  if (block.matches(FOCUSABLE_CONTROL) || block.matches('[role="radio"]')) {
    return block;
  }
  return (
    block.querySelector<HTMLElement>(INVALID_CONTROL) ??
    block.querySelector<HTMLElement>('[role="radio"]:not([disabled])') ??
    block.querySelector<HTMLElement>(FOCUSABLE_CONTROL)
  );
}

/** Frames without movement that end a smooth scroll short of its target. */
const SETTLE_STILL_FRAMES = 6;
/** The latest a blocked Next's focus waits for its smooth scroll. */
export const BLOCKED_FOCUS_MAX_WAIT_MS = 1_200;

/**
 * A blocked Next (switch-on follow-up): bring `block` into the band above
 * the bottom chrome exactly as `scrollBlockAboveBottomChrome` does, then
 * focus its field (`blockedFieldFocusTarget`, or `opts.field`) without
 * scrolling, so the visitor lands on the answer that is missing, as the
 * station request's blocked Send already did.
 *
 * A smooth scroll is let finish first. Focused at once, a text field would
 * set off the app-wide focus helper, which pulls it to the band's edge on
 * focusin and cuts the smooth scroll short (round 2); focused once the
 * block is in place, the helper finds it inside the band and leaves the
 * scroll where it is. A wheel or touch scroll meanwhile, or focus the
 * visitor moved elsewhere, drops the focus.
 *
 * Returns a cancel for the pending focus.
 */
export function scrollBlockAboveBottomChromeAndFocus(
  block: HTMLElement | null,
  opts: {
    first?: HTMLElement | null;
    last?: HTMLElement | null;
    behavior?: ScrollBehavior;
    field?: HTMLElement | null;
  } = {},
): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }
  const field =
    opts.field !== undefined ? opts.field : blockedFieldFocusTarget(block);
  const scroller =
    block && block.isConnected ? nearestVerticalScroller(block) : null;
  const startTop = scroller?.scrollTop ?? 0;
  const before = document.activeElement;
  const applied = scrollBlockAboveBottomChrome(block, opts);

  let done = false;
  let raf = 0;
  let timer = 0;
  const stop = () => {
    done = true;
    if (raf) window.cancelAnimationFrame(raf);
    if (timer) window.clearTimeout(timer);
    window.removeEventListener("wheel", stop, true);
    window.removeEventListener("touchmove", stop, true);
  };
  const focus = () => {
    if (done) return;
    stop();
    if (!field || !field.isConnected) return;
    const active = document.activeElement;
    if (active && active !== before && active !== document.body) return;
    field.focus({ preventScroll: true });
  };
  if (!scroller || applied === 0 || opts.behavior !== "smooth") {
    focus();
    return stop;
  }
  const target = startTop + applied;
  let last = scroller.scrollTop;
  let still = 0;
  const watch = () => {
    raf = 0;
    if (done) return;
    const now = scroller.scrollTop;
    if (Math.abs(now - target) < 1) {
      focus();
      return;
    }
    still = now === last ? still + 1 : 0;
    last = now;
    // Stopped short of the target (the page moved under it): focus anyway.
    if (still >= SETTLE_STILL_FRAMES) {
      focus();
      return;
    }
    raf = window.requestAnimationFrame(watch);
  };
  window.addEventListener("wheel", stop, { passive: true, capture: true });
  window.addEventListener("touchmove", stop, { passive: true, capture: true });
  raf = window.requestAnimationFrame(watch);
  timer = window.setTimeout(focus, BLOCKED_FOCUS_MAX_WAIT_MS);
  return stop;
}

export function scheduleKeyboardScrollIntoView(
  el: EventTarget | null,
  timers: { raf?: number[]; timeout?: number[] } = {},
): () => void {
  const run = () => {
    if (!(el instanceof HTMLElement)) return;
    if (
      document.activeElement !== el &&
      !(
        document.activeElement instanceof Node &&
        el.contains(document.activeElement)
      )
    ) {
      return;
    }
    scrollFocusedFieldIntoView(el);
  };

  run();
  const rafIds: number[] = timers.raf ?? [];
  const timeoutIds: number[] = timers.timeout ?? [];
  if (typeof window.requestAnimationFrame === "function") {
    rafIds.push(
      window.requestAnimationFrame(() => {
        run();
        rafIds.push(window.requestAnimationFrame(run));
      }),
    );
  }
  timeoutIds.push(window.setTimeout(run, 80));
  timeoutIds.push(window.setTimeout(run, 280));

  return () => {
    for (const id of rafIds) window.cancelAnimationFrame(id);
    for (const id of timeoutIds) window.clearTimeout(id);
  };
}
